/**
 * useFocusHandoff — keeps a student's place through a click whose result
 * replaces the control they used: a button turns busy, a result panel takes
 * its place, and a retry or a dismiss can bring the button back. CheckoutFlow
 * is the first user.
 *
 * Each of those swaps takes the focused control off the page, so focus falls
 * to <body>: a keyboard or screen-reader student loses their place, and a
 * role="status" panel that appears already filled is not reliably announced.
 *
 *   const { landing, arm, disarm } = useFocusHandoff(view, inFlight);
 *   <Button {...landing} onClick={(e) => { arm(e.currentTarget); start(); }}>
 *   <p tabIndex={-1} {...landing}>{message}</p>
 *
 * The rule:
 *   - `arm(control)` runs in the click handler, before the state change. A
 *     click that left focus nowhere (Safari and iOS focus no button on click)
 *     puts it on the control first, so a dialog the click opens has an opener
 *     to return to. It then records where focus is.
 *   - `view` names what is on screen. When it changes with a hand-off armed,
 *     and focus was lost or is still where the hand-off left it, focus moves
 *     to the element carrying `landing`. Focus on any other live element is
 *     the student's own: nothing moves, and the hand-off is spent.
 *   - While `inFlight` (the request is still running) a control that survived
 *     the change keeps focus and only lost focus lands; that landing is then
 *     where the hand-off left focus. The hand-off stays armed for the outcome,
 *     which lands and spends it.
 *   - Only a click arms it. A view that changes by itself (data arriving)
 *     moves nothing unless a click's hand-off is still pending, and
 *     `disarm()` drops a pending one (a bfcache restore, whose request is
 *     dead).
 *
 * `landing` also carries refuseHeldEnter as its onKeyDown. A landing can be a
 * live button, and the Enter that started all this may still be down: its next
 * repeat would press that button. An element that needs an onKeyDown of its
 * own must call refuseHeldEnter from it; one written after the spread would
 * replace the guard.
 *
 * A layout effect, so focus lands in the commit that changed the view. No key
 * press can reach <body> in between, and a dialog closing in that same commit
 * restores focus afterwards (useRestoreFocus then finds focus already inside
 * the dialog left open, or on the opener, and leaves it).
 *
 * See also useGateFocus, for a view that swaps itself for a gate panel with no
 * click to arm from.
 */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import refuseHeldEnter from '../utils/refuseHeldEnter';

// Focus is on nothing the student can act from. That is always <body> (or
// <html>): a focused node that leaves the page hands focus there.
function focusIsLost(active) {
  return !active || active === document.body || active === document.documentElement;
}

function useFocusHandoff(view, inFlight) {
  const landingRef = useRef(null);
  // null, or { held }: the element the hand-off left focus on. That is where
  // it was when the click armed it, or an in-flight landing since.
  const handoffRef = useRef(null);

  useLayoutEffect(() => {
    const handoff = handoffRef.current;
    if (!handoff) return;
    const active = document.activeElement;
    const lost = focusIsLost(active);
    if (!lost && active !== handoff.held) {
      handoffRef.current = null;
      return;
    }
    if (inFlight) {
      if (lost) {
        landingRef.current?.focus();
        // The hand-off holds its own landing now: still focused at the
        // outcome, it is not focus the student moved.
        handoff.held = document.activeElement;
      }
      return;
    }
    landingRef.current?.focus();
    handoffRef.current = null;
  }, [view, inFlight]);

  const arm = useCallback((control) => {
    if (control && focusIsLost(document.activeElement)) control.focus();
    handoffRef.current = { held: document.activeElement };
  }, []);

  const disarm = useCallback(() => {
    handoffRef.current = null;
  }, []);

  const landing = useMemo(() => ({ ref: landingRef, onKeyDown: refuseHeldEnter }), []);

  return { landing, arm, disarm };
}

export default useFocusHandoff;
